import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUp, MessageSquare, Send, CheckCircle2 } from "lucide-react";
import { api, absUrl, formatApiError } from "@/lib/api";
import { CATEGORIES, timeAgo } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import ReportButton from "@/components/ReportButton";
import Seo, { articleSchema } from "@/components/Seo";

function flattenComments(roots, childrenMap, depth = 0) {
    const out = [];
    for (const c of roots) {
        out.push({ c, depth });
        const kids = childrenMap[c.comment_id] || [];
        if (kids.length) {
            out.push(...flattenComments(kids, childrenMap, Math.min(depth + 1, 3)));
        }
    }
    return out;
}

export default function PostDetail() {
    const { id } = useParams();
    const [post, setPost] = useState(null);
    const [comments, setComments] = useState([]);
    const [body, setBody] = useState("");
    const [replyTo, setReplyTo] = useState(null);
    const { user } = useAuth();
    const navigate = useNavigate();

    async function load() {
        try {
            const [{ data: p }, { data: c }] = await Promise.all([
                api.get(`/posts/${id}`),
                api.get(`/posts/${id}/comments`),
            ]);
            setPost(p);
            setComments(c);
        } catch (e) {
            toast.error(formatApiError(e));
        }
    }
    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    async function vote(direction) {
        if (!user) return navigate("/login");
        const nextDir = post.my_vote === direction ? 0 : direction;
        const { data } = await api.post(`/posts/${post.post_id}/vote`, { direction: nextDir });
        setPost((p) => ({ ...p, upvotes: data.upvotes, my_vote: data.my_vote }));
    }

    async function submitComment(e) {
        e.preventDefault();
        if (!user) return navigate("/login");
        if (!body.trim()) return;
        try {
            const { data } = await api.post(`/posts/${post.post_id}/comments`, {
                body: body.trim(),
                parent_id: replyTo,
            });
            setComments((c) => [...c, data]);
            setBody("");
            setReplyTo(null);
            setPost((p) => ({ ...p, comment_count: (p.comment_count || 0) + 1 }));
        } catch (e2) {
            toast.error(formatApiError(e2));
        }
    }

    if (!post) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-onyx-900">
                <div className="w-10 h-10 border-2 border-onyx-600 border-t-hellcat rounded-full animate-spin" />
            </div>
        );
    }

    const roots = comments.filter((c) => !c.parent_id);
    const childrenMap = comments.reduce((acc, c) => {
        if (c.parent_id) (acc[c.parent_id] = acc[c.parent_id] || []).push(c);
        return acc;
    }, {});
    const flat = flattenComments(roots, childrenMap);

    return (
        <div className="min-h-screen bg-onyx-900">
            <Seo
                title={`${post.title} — ${CATEGORIES.find((c) => c.value === post.category)?.label || "SRT Community"}`}
                description={
                    (post.body || `SRT Finder community discussion in ${CATEGORIES.find((c) => c.value === post.category)?.label} — join the conversation about Dodge Hellcat, SRT, R/T, TRX, RHO, Redeye, Widebody, Demon, and Scat Pack builds.`).slice(0, 300)
                }
                canonicalPath={`/community/${post.post_id}`}
                type="article"
                image={post.photos?.[0]}
                imageAlt={post.title}
                jsonLd={articleSchema(post)}
                breadcrumbs={[
                    { name: "Home", path: "/" },
                    { name: "Community", path: "/community" },
                    {
                        name: CATEGORIES.find((c) => c.value === post.category)?.label || "Post",
                        path: `/community?category=${post.category}`,
                    },
                    { name: post.title, path: `/community/${post.post_id}` },
                ]}
            />
            <div
                className="sticky top-0 z-30 backdrop-blur-xl border-b border-onyx-600"
                style={{ background: "rgba(10,10,10,0.85)" }}
            >
                <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-2">
                    <button
                        onClick={() => navigate(-1)}
                        className="btn-ghost rounded-sm px-2.5 py-2"
                        data-testid="back-btn"
                    >
                        <ArrowLeft className="w-4 h-4" />
                    </button>
                    <div className="headline text-lg truncate">Post</div>
                </div>
            </div>

            <div className="max-w-3xl mx-auto px-4 py-5">
                <div className="surface rounded-md p-5">
                    <div className="flex items-center gap-2 text-xs text-onyx-500 mb-2">
                        <span
                            className="rounded-sm px-1.5 py-0.5 bg-onyx-800 uppercase tracking-widest text-[10px]"
                            style={{ letterSpacing: "0.15em" }}
                        >
                            {CATEGORIES.find((c) => c.value === post.category)?.label}
                        </span>
                        <span>-</span>
                        <Link to={`/users/${post.author?.user_id}`} className="hover:text-white">
                            {post.author?.name}
                        </Link>
                        <span>-</span>
                        <span>{timeAgo(post.created_at)}</span>
                    </div>
                    <h1 className="headline text-3xl">{post.title}</h1>
                    {post.body && (
                        <p className="mt-3 text-sm text-onyx-300 leading-relaxed whitespace-pre-wrap">
                            {post.body}
                        </p>
                    )}
                    {post.photos?.length > 0 && (
                        <div className="mt-4 grid grid-cols-1 gap-3">
                            {post.photos.map((p, i) => (
                                <img
                                    key={p + i}
                                    src={absUrl(p)}
                                    alt=""
                                    className="w-full rounded-sm surface-elevated"
                                />
                            ))}
                        </div>
                    )}
                    <div className="mt-5 flex items-center gap-3">
                        <button
                            onClick={() => vote(1)}
                            data-testid="post-upvote-btn"
                            className={`inline-flex items-center gap-1.5 rounded-sm px-3 py-2 text-sm font-bold uppercase tracking-widest ${
                                post.my_vote === 1
                                    ? "bg-hellcat text-white"
                                    : "surface-elevated text-white"
                            }`}
                            style={{ transition: "background-color 0.15s" }}
                        >
                            <ArrowUp className="w-4 h-4" /> {post.upvotes}
                        </button>
                        <div className="flex items-center gap-1.5 text-onyx-500 text-sm">
                            <MessageSquare className="w-4 h-4" /> {post.comment_count}
                        </div>
                        <div className="ml-auto flex items-center gap-2">
                            {user && post.owner_id === user.user_id && (
                                <button
                                    type="button"
                                    data-testid="toggle-solved-btn"
                                    onClick={async () => {
                                        try {
                                            if (post.solved) {
                                                await api.delete(`/posts/${post.post_id}/solved`);
                                                setPost({ ...post, solved: false });
                                                toast.success("Marked unsolved");
                                            } else {
                                                await api.post(`/posts/${post.post_id}/solved`, {});
                                                setPost({ ...post, solved: true });
                                                toast.success("Marked solved");
                                            }
                                        } catch (e) {
                                            toast.error(formatApiError(e));
                                        }
                                    }}
                                    className="inline-flex items-center gap-1.5 rounded-sm px-3 py-2 text-xs font-bold uppercase tracking-widest surface-elevated text-white"
                                    style={{
                                        color: post.solved ? "#86efac" : undefined,
                                        borderColor: post.solved ? "rgba(74,222,128,0.4)" : undefined,
                                    }}
                                >
                                    <CheckCircle2 className="w-3.5 h-3.5" />
                                    {post.solved ? "Solved" : "Mark solved"}
                                </button>
                            )}
                            <ReportButton
                                targetType="post"
                                targetId={post.post_id}
                                testid="report-post-btn"
                            />
                        </div>
                    </div>
                </div>

                <div className="mt-6">
                    <div className="label-caps mb-3">Comments</div>
                    <form onSubmit={submitComment} className="surface rounded-md p-3 mb-4">
                        {replyTo && (
                            <div className="text-xs text-onyx-500 mb-1 flex items-center gap-2">
                                Replying{" - "}
                                <button
                                    type="button"
                                    onClick={() => setReplyTo(null)}
                                    className="text-hellcat"
                                >
                                    Cancel
                                </button>
                            </div>
                        )}
                        <textarea
                            data-testid="comment-input"
                            value={body}
                            onChange={(e) => setBody(e.target.value)}
                            placeholder={user ? "Write a comment..." : "Sign in to reply"}
                            disabled={!user}
                            className="w-full bg-transparent border-0 text-sm p-2 min-h-[70px] focus:outline-none placeholder:text-onyx-500 resize-none"
                        />
                        <div className="flex justify-end">
                            <button
                                type="submit"
                                data-testid="comment-submit"
                                disabled={!user || !body.trim()}
                                className="btn-red rounded-sm px-3 py-2 inline-flex items-center gap-2 text-sm disabled:opacity-40"
                            >
                                <Send className="w-3.5 h-3.5" /> Post
                            </button>
                        </div>
                    </form>

                    {flat.length === 0 ? (
                        <div className="text-sm text-onyx-500 text-center py-6">
                            Be the first to comment.
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {flat.map(({ c, depth }) => (
                                <div
                                    key={c.comment_id}
                                    className="surface rounded-md p-4"
                                    style={{ marginLeft: depth * 16 }}
                                >
                                    <div className="flex items-center gap-2 text-xs text-onyx-500 mb-1">
                                        <Link
                                            to={`/users/${c.author?.user_id}`}
                                            className="text-white font-bold"
                                        >
                                            {c.author?.name}
                                        </Link>
                                        <span>-</span>
                                        <span>{timeAgo(c.created_at)}</span>
                                    </div>
                                    <div className="text-sm text-onyx-300 whitespace-pre-wrap">
                                        {c.body}
                                    </div>
                                    {depth < 3 && (
                                        <button
                                            onClick={() => setReplyTo(c.comment_id)}
                                            className="text-xs text-hellcat mt-2 label-caps"
                                            data-testid={`reply-${c.comment_id}`}
                                        >
                                            Reply
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
